import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useVideoRefs } from "./useVideoRefs";
import type { CameraConfig } from "../services/StorageService";

export type CameraState = "initializing" | "ready" | "error" | "unavailable";

export interface CameraDevice {
  deviceId: string;
  label: string;
  isExternal: boolean;
}

interface UseCameraInitializationOptions {
  initializationTimeout?: number;
  selectedDeviceIds?: (string | null)[]; // Array with slot indices preserved (null for empty slots)
  cameraConfig: CameraConfig; // Camera configuration including enhancement settings
}

interface UseCameraInitializationResult {
  videoRef1: React.RefObject<HTMLVideoElement | null>;
  videoRef2: React.RefObject<HTMLVideoElement | null>;
  videoRef3: React.RefObject<HTMLVideoElement | null>;
  videoRefs: React.RefObject<HTMLVideoElement | null>[]; // For backward compatibility
  cameraInitializationState: CameraState[];
  availableCameras: number;
  availableDevices: CameraDevice[]; // List of all available camera devices
  initializeCameras: (selectedDeviceIds?: (string | null)[]) => Promise<void>;
  cleanupCameraStreams: () => void;
  handleVideoError: (index: number) => void;
  enumerateDevices: () => Promise<CameraDevice[]>; // Function to get available devices
  detachAllStreams: () => void; // Add this method
  attachStream: (index: number, stream: MediaStream) => void; // Add this method
  setEnhancementEnabled: (enabled: boolean) => void; // Enable/disable video enhancement
}

/**
 * Check if a device is likely an external webcam based on its label
 */
const isLikelyExternalWebcam = (deviceLabel: string): boolean => {
  const label = deviceLabel.toLowerCase();

  // Common keywords that indicate external webcams
  const externalKeywords = [
    "usb",
    "external",
    "logitech",
    "microsoft",
    "razer",
    "creative",
    "trust",
    "hp webcam",
    "dell webcam",
    "c920",
    "c922",
    "c925",
    "c930", // Common Logitech models
    "lifecam",
    "quickcam",
  ];

  // Keywords that usually indicate built-in webcams
  const builtInKeywords = [
    "built-in",
    "builtin",
    "integrated",
    "internal",
    "facetime",
    "front camera",
    "laptop camera",
    "uvc webcam", // Added "UVC WebCam" as built-in indicator
    "uvc camera",
  ];

  // Special case: 'webcam' or 'web cam' only indicates external if not preceded by 'uvc'
  let isGenericWebcam = false;
  if (
    (label.includes("webcam") || label.includes("web cam")) &&
    !label.includes("uvc")
  ) {
    isGenericWebcam = true;
  }

  // Check for external keywords
  const hasExternalKeyword =
    externalKeywords.some((keyword) => label.includes(keyword)) ||
    isGenericWebcam;

  // Check for built-in keywords
  const hasBuiltInKeyword = builtInKeywords.some((keyword) =>
    label.includes(keyword)
  );

  // Log the detection result for debugging
  console.log(
    `Camera "${deviceLabel}" detection: built-in keywords: ${hasBuiltInKeyword}, external keywords: ${hasExternalKeyword}`
  );

  // If it explicitly has built-in keywords, it's probably not external
  if (hasBuiltInKeyword) return false;

  // If it has external keywords, it's likely external
  return hasExternalKeyword;
};

export const useCameraInitialization = (
  options: UseCameraInitializationOptions
): UseCameraInitializationResult => {
  const { initializationTimeout = 10000, selectedDeviceIds, cameraConfig } = options;

  // Use our custom video refs hook with explicit refs and slot configs
  const {
    videoRef1,
    videoRef2,
    videoRef3,
    attachStream,
    detachStream,
    detachAllStreams,
    setEnhancementEnabled,
  } = useVideoRefs({
    slotConfigs: [cameraConfig.slot1, cameraConfig.slot2, cameraConfig.slot3],
  });

  // Create an array of refs for compatibility with existing code
  // Memoize to prevent changing on every render
  const videoRefs = useMemo(
    () => [videoRef1, videoRef2, videoRef3],
    [videoRef1, videoRef2, videoRef3]
  );

  const [availableCameras, setAvailableCameras] = useState<number>(0);
  const [availableDevices, setAvailableDevices] = useState<CameraDevice[]>([]);
  const [cameraInitializationState, setCameraInitializationState] = useState<
    CameraState[]
  >(["initializing", "initializing", "initializing"]);

  // Track used device IDs to prevent conflicts
  const usedDeviceIdsRef = useRef<Set<string>>(new Set());

  // Track initialization attempts to prevent infinite loops
  const initializationAttemptsRef = useRef<number>(0);

  // Track timeouts so we can clear them when needed
  const timeoutIdsRef = useRef<number[]>([]);

  // Cache for device enumeration to avoid repeated permission requests
  const deviceCacheRef = useRef<{
    devices: CameraDevice[];
    timestamp: number;
    hasPermission: boolean;
  }>({
    devices: [],
    timestamp: 0,
    hasPermission: false,
  });

  // Update a single camera's state
  const updateCameraState = useCallback((index: number, state: CameraState) => {
    setCameraInitializationState((prev) => {
      const newState = [...prev];
      newState[index] = state;
      return newState;
    });
  }, []);

  // Enumerate available camera devices with caching and optimization
  const enumerateDevices = useCallback(
    async (forceRefresh = false): Promise<CameraDevice[]> => {
      try {
        const now = Date.now();
        const CACHE_DURATION = 30000; // Cache for 30 seconds

        // Use cached devices if recent and not forcing refresh
        if (
          !forceRefresh &&
          deviceCacheRef.current.devices.length > 0 &&
          now - deviceCacheRef.current.timestamp < CACHE_DURATION
        ) {
          console.log("Using cached camera devices");
          setAvailableDevices(deviceCacheRef.current.devices);
          setAvailableCameras(deviceCacheRef.current.devices.length);
          return deviceCacheRef.current.devices;
        }

        console.log("Enumerating camera devices...");

        // Check if we already have permissions by trying to enumerate first
        let devices = await navigator.mediaDevices.enumerateDevices();
        const hasLabels = devices.some(
          (device) => device.kind === "videoinput" && device.label
        );

        // Only request permission if we don't have device labels and haven't gotten permission before
        if (!hasLabels && !deviceCacheRef.current.hasPermission) {
          console.log("Requesting camera permissions for device labels...");
          try {
            const initialStream = await navigator.mediaDevices.getUserMedia({
              video: true,
              audio: false,
            });

            // Stop this stream immediately after permission
            initialStream.getTracks().forEach((track) => track.stop());
            deviceCacheRef.current.hasPermission = true;

            // Add small delay to ensure permission is processed
            await new Promise((resolve) => setTimeout(resolve, 200));

            // Re-enumerate to get proper labels
            devices = await navigator.mediaDevices.enumerateDevices();
          } catch (permErr) {
            console.warn(
              "Permission request failed, using devices without labels:",
              permErr
            );
          }
        }

        // Filter to just video devices and create our device objects
        const videoDevices = devices
          .filter((device) => device.kind === "videoinput")
          .map((device) => ({
            deviceId: device.deviceId,
            label: device.label || `Camera ${device.deviceId.slice(0, 8)}...`,
            isExternal: isLikelyExternalWebcam(device.label || ""),
          }));

        // Sort to prioritize external cameras first
        videoDevices.sort((a, b) => {
          if (a.isExternal && !b.isExternal) return -1;
          if (!a.isExternal && b.isExternal) return 1;
          return 0;
        });

        // Update cache
        deviceCacheRef.current = {
          devices: videoDevices,
          timestamp: now,
          hasPermission: deviceCacheRef.current.hasPermission,
        };

        console.log("Available camera devices:", videoDevices);
        setAvailableDevices(videoDevices);
        setAvailableCameras(videoDevices.length);

        return videoDevices;
      } catch (err) {
        console.error("Failed to enumerate camera devices:", err);
        // Return cached devices on error if available
        if (deviceCacheRef.current.devices.length > 0) {
          return deviceCacheRef.current.devices;
        }
        return [];
      }
    },
    [] // isLikelyExternalWebcam is a stable function, not a dependency
  );

  // Initialize cameras with optional device selection
  const initializeCameras = useCallback(
    async (customSelectedDeviceIds?: (string | null)[]) => {
      try {
        // Clear previous state
        initializationAttemptsRef.current++;
        const currentAttempt = initializationAttemptsRef.current;

        // Clear any pending timeouts
        timeoutIdsRef.current.forEach((id) => window.clearTimeout(id));
        timeoutIdsRef.current = [];

        // Reset all camera states to "initializing"
        setCameraInitializationState([
          "initializing",
          "initializing",
          "initializing",
        ]);

        // First cleanup any existing streams to start fresh
        detachAllStreams();

        // Clear the used devices tracking
        usedDeviceIdsRef.current.clear();

        console.log("=== Starting camera initialization ===");

        // Get devices to use - either from parameter, options, or auto-detect
        const targetDeviceIds = customSelectedDeviceIds || selectedDeviceIds;

        // Map slot indices to devices
        let slotAssignments: (CameraDevice | null)[];

        if (targetDeviceIds && targetDeviceIds.length > 0) {
          // Use specifically selected devices with slot preservation
          const allDevices =
            availableDevices.length > 0
              ? availableDevices
              : await enumerateDevices();

          // Map each slot's deviceId to its CameraDevice, preserving nulls
          slotAssignments = targetDeviceIds.map((deviceId) =>
            deviceId ? allDevices.find((d) => d.deviceId === deviceId) || null : null
          );
          console.log("Using selected devices with slot assignments:", slotAssignments);
        } else {
          // Auto-detect and use all available devices (assign sequentially)
          const devicesToUse =
            availableDevices.length > 0
              ? availableDevices
              : await enumerateDevices();
          slotAssignments = [
            devicesToUse[0] || null,
            devicesToUse[1] || null,
            devicesToUse[2] || null,
          ];
          console.log("Using all available devices:", slotAssignments);
        }

        // Check if any cameras are configured
        const hasAnyCamera = slotAssignments.some((device) => device !== null);
        if (!hasAnyCamera) {
          console.error("No camera devices available for initialization");
          setCameraInitializationState([
            "unavailable",
            "unavailable",
            "unavailable",
          ]);
          return;
        }

        // Initialize each slot sequentially
        for (let slotIndex = 0; slotIndex < 3; slotIndex++) {
          // Skip if initialization attempt has changed
          if (currentAttempt !== initializationAttemptsRef.current) {
            console.log("Initialization attempt changed, aborting");
            return;
          }

          const deviceToUse = slotAssignments[slotIndex];

          // If this slot has no device assigned, mark as unavailable
          if (!deviceToUse) {
            updateCameraState(slotIndex, "unavailable");
            console.log(`Slot ${slotIndex} has no camera assigned`);
            continue;
          }

          // Add delay between camera initializations (only for slots that have cameras)
          // Count how many cameras we've initialized before this slot
          const previousCamerasCount = slotAssignments
            .slice(0, slotIndex)
            .filter((d) => d !== null).length;
          const delay = previousCamerasCount > 0 ? 2000 : 1500; // 2 seconds between cameras
          console.log(`Waiting ${delay}ms before initializing camera at slot ${slotIndex}`);
          await new Promise((resolve) => setTimeout(resolve, delay));

          console.log(
            `Initializing camera at slot ${slotIndex} with device: ${deviceToUse.label} (${deviceToUse.deviceId})`
          );

          try {
            // First detach any existing stream for this slot
            detachStream(slotIndex);

            // Mark as initializing
            updateCameraState(slotIndex, "initializing");

            // Create constraints for this specific device
            const constraints = {
              video: {
                deviceId: { exact: deviceToUse.deviceId },
                width: { ideal: 1280 },
                height: { ideal: 720 },
                frameRate: { ideal: 30 },
              },
              audio: false,
            };

            // Request the stream with timeout
            const stream = (await Promise.race([
              navigator.mediaDevices.getUserMedia(constraints),
              new Promise<never>((_, reject) => {
                const id = setTimeout(() => {
                  reject(new Error(`Camera at slot ${slotIndex} initialization timed out`));
                }, initializationTimeout);
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                timeoutIdsRef.current.push(id);
              }),
            ])) as MediaStream;

            console.log(`Successfully accessed camera at slot ${slotIndex}`);

            // Force these base settings to avoid variability. DO NOT CHANGE THESE VALUES.
            const videoTrack = stream.getVideoTracks()[0];
            if (videoTrack) {
              try {
                const capabilities = videoTrack.getCapabilities();
                console.log(`Camera capabilities for slot ${slotIndex}:`, capabilities);

                const advancedConstraints: Record<string, number> = {};

                if ('brightness' in capabilities) {
                  advancedConstraints.brightness = 100;
                }

                if ('sharpness' in capabilities) {
                  advancedConstraints.sharpness = 100;
                }

                if ('saturation' in capabilities) {
                  advancedConstraints.saturation = 0;
                }

                if ('contrast' in capabilities) {
                  advancedConstraints.contrast = 128;
                }

                // Apply the constraints if any are supported
                if (Object.keys(advancedConstraints).length > 0) {
                  await videoTrack.applyConstraints(advancedConstraints as unknown as MediaTrackConstraints);
                  console.log(`Applied camera settings for slot ${slotIndex}:`, advancedConstraints);
                } else {
                  console.warn(`No advanced camera controls supported for slot ${slotIndex}`);
                }
              } catch (constraintErr) {
                console.warn(`Failed to apply camera settings for slot ${slotIndex}:`, constraintErr);
              }
            }

            // Add to used devices set
            usedDeviceIdsRef.current.add(deviceToUse.deviceId);

            // Assign to video element at the correct slot index
            attachStream(slotIndex, stream);

            // Wait for video to be ready
            const videoRef = videoRefs[slotIndex];
            if (videoRef?.current) {
              await new Promise<void>((resolve, reject) => {
                if (!videoRef.current)
                  return reject(new Error("Video element not available"));

                const onMetadataLoaded = () => {
                  console.log(`Video at slot ${slotIndex} metadata loaded`);
                  videoRef.current?.removeEventListener(
                    "loadedmetadata",
                    onMetadataLoaded
                  );
                  resolve();
                };

                const onError = (e: Event) => {
                  console.error(`Video at slot ${slotIndex} error:`, e);
                  videoRef.current?.removeEventListener("error", onError);
                  reject(new Error("Video element error"));
                };

                videoRef.current.addEventListener(
                  "loadedmetadata",
                  onMetadataLoaded
                );
                videoRef.current.addEventListener("error", onError);

                // Already loaded?
                if (videoRef.current.readyState >= 1) {
                  resolve();
                }

                // Timeout for video ready state
                setTimeout(() => {
                  reject(new Error(`Video at slot ${slotIndex} ready state timeout`));
                }, 5000);
              });
            }

            // Mark as ready
            updateCameraState(slotIndex, "ready");
            console.log(`Camera at slot ${slotIndex} initialization completed successfully`);
          } catch (err) {
            console.error(`Failed to initialize camera at slot ${slotIndex}:`, err);
            updateCameraState(slotIndex, "error");
          }
        }

        console.log("=== Camera initialization complete ===");
      } catch (err) {
        console.error("Critical error during camera initialization:", err);
        setCameraInitializationState(["error", "error", "error"]);
      }
    },
    [
      availableDevices,
      selectedDeviceIds,
      enumerateDevices,
      detachAllStreams,
      detachStream,
      attachStream,
      updateCameraState,
      videoRefs,
      initializationTimeout,
    ]
  );

  // Handle video element errors
  const handleVideoError = useCallback(
    (index: number) => {
      console.log(`Video element ${index} error`);
      updateCameraState(index, "error");
    },
    [updateCameraState]
  );

  // Clean up on unmount
  useEffect(() => {
    return () => {
      // Clear any pending timeouts
      timeoutIdsRef.current.forEach((id) => clearTimeout(id));
      detachAllStreams();
    };
  }, [detachAllStreams]);

  return {
    videoRef1,
    videoRef2,
    videoRef3,
    videoRefs,
    cameraInitializationState,
    availableCameras,
    availableDevices,
    initializeCameras,
    cleanupCameraStreams: detachAllStreams,
    handleVideoError,
    enumerateDevices,
    detachAllStreams, // Expose detachAllStreams
    attachStream, // Expose attachStream
    setEnhancementEnabled, // Expose setEnhancementEnabled
  };
};
